import { useMemo } from 'react';
import { FilterableHistoryType } from 'src/@types/historyTables';
import useHistoryTableFilterStore from 'src/stores/historyTableFilterStore/useHistoryTableFilterStore';
import useCurrentAuthority from '../useCurrentAuthority';
import useVelocityAccountStore from 'src/stores/useVelocityAccountsStore';
import DataApiClient from 'src/utils/DataApiClient';
import { createQueryKey } from 'src/utils/query/createQueryKey';
import { useQuery } from '@tanstack/react-query';

interface UseLongTermHistoryQueryResult<T> {
	/** All records fetched */
	allRecords: T[];
	/** Whether the query is loading */
	isLoading: boolean;
	/** Whether the query is fetching (including background refetch) */
	isFetching: boolean;
	/** Whether at least one fetch has completed */
	isFetched: boolean;
	/** Refetch the query */
	refetch: () => void;
}

/**
 * Pure data fetching hook for long-term history data.
 * Uses React Query's useQuery.
 * Does NOT manage UI state - only fetches and returns data.
 */
export const useLongTermHistoryQuery = <T,>(
	historyType: FilterableHistoryType,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	deserializer: (r: any) => T | null,
	enabled: boolean
): UseLongTermHistoryQueryResult<T> => {
	const currentAccountPubKey = useVelocityAccountStore((s) =>
		s.getCurrentUserAccount()?.pubKey?.toString()
	);
	const currentAuthority = useCurrentAuthority();

	// Get date filters from the filter store (read-only)
	const startDate = useHistoryTableFilterStore(
		(s) => s.getFilterState(historyType).startDate
	);
	const endDate = useHistoryTableFilterStore(
		(s) => s.getFilterState(historyType).endDate
	);

	const userAccount = currentAccountPubKey ?? '';
	const authorityStr = currentAuthority?.toString() ?? '';

	const queryEnabled = enabled && userAccount !== '' && authorityStr !== '';

	const query = useQuery({
		queryKey: createQueryKey(
			'long-term-history',
			historyType,
			userAccount,
			authorityStr,
			startDate,
			endDate
		),
		queryFn: async () => {
			const res = await DataApiClient.fetchLongTermData<T>(historyType, {
				userAccount,
				authority: authorityStr,
				startDate: startDate ?? undefined,
				endDate: endDate ?? undefined,
			});
			return res.records
				.map((r) => deserializer(r as unknown as Record<string, unknown>))
				.filter((r): r is T => r !== null);
		},
		enabled: queryEnabled,
		refetchOnWindowFocus: false,
		staleTime: 30_000,
	});

	const allRecords = useMemo(() => {
		return query.data ?? [];
	}, [query.data]);

	return {
		allRecords,
		isLoading: query.isLoading,
		isFetching: query.isFetching,
		isFetched: query.isFetched,
		refetch: query.refetch,
	};
};
